import { computed, effect, signal } from '@preact/signals';

import type { AuthContext, EmailCodeChallenge, HouseholdView } from '@amumin/api-client';
import { duplicateColorFixes } from '@amumin/domain';
import {
  appMessagesFor,
  effectiveDigits,
  effectiveLocale,
  intlTag,
  isLocaleCode,
  localeInfo,
  messagesFor,
  normalizeLocale,
  setDisplayDigits,
  weatherPrayerFor,
  type LocaleCode,
} from '@amumin/i18n';

import { api, isApiError, setRequestLocale } from '../api.js';
import { rememberPairingLocation, clearPairingResume } from '../lib/pairingResume.js';
import { moduleOn } from './modules.js';

// Sessiestaat van de ouderapp als signals (ONTWERP.md §3.1). De server is de bron van waarheid:
// `context` is een cache van GET /v1/app/auth/context en wordt na elke login/step-up ververst.

export type AuthState = 'unknown' | 'loading' | 'authenticated' | 'anonymous';

export const authState = signal<AuthState>('unknown');
export const context = signal<AuthContext | null>(null);
export const household = signal<HouseholdView | null>(null);

const PENDING_KEY = 'fb.pending_challenge';

/** Leest de openstaande challenge terug na een refresh (alleen uitleg; het geheim is fb_chal). */
function restorePendingChallenge(): EmailCodeChallenge | null {
  try {
    const raw = sessionStorage.getItem(PENDING_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as EmailCodeChallenge;
    if (
      parsed.status !== 'email_code_required' ||
      new Date(parsed.expires_at).getTime() < Date.now()
    ) {
      sessionStorage.removeItem(PENDING_KEY);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

/** Openstaande e-mailcode-challenge (cookie fb_chal staat in de browser; hier alleen de uitleg). */
export const pendingChallenge = signal<EmailCodeChallenge | null>(restorePendingChallenge());

export function setPendingChallenge(challenge: EmailCodeChallenge | null): void {
  pendingChallenge.value = challenge;
  try {
    if (challenge) sessionStorage.setItem(PENDING_KEY, JSON.stringify(challenge));
    else sessionStorage.removeItem(PENDING_KEY);
  } catch {
    // privémodus of geblokkeerde opslag: de pagina werkt zonder (alleen geen refresh-herstel)
  }
}
/** Vinkje "gedeelde computer" bij de laatste login → banner "vergeet niet uit te loggen". */
export const sharedComputer = signal(false);

/**
 * Review golf 8 r2: net een uitnodiging aangenomen met een nieuw wachtwoord? Dan staat het
 * e-mailadres al ingevuld op /login, met "Log in met je nieuwe wachtwoord". Alleen in het geheugen
 * (nooit in de URL of in opslag).
 */
export const loginHint = signal<{ email: string } | null>(null);

// ---- Taal (golf 3, 0033_talen; docs/DESIGN.md §8.T) -------------------------------------------
// Ingelogd: de eigen taal van het gezinslid van deze login (module Talen aan), anders die van het
// gezin. Vóór het inloggen (en tijdens het laden): de laatst gekozen taal in deze browser, anders
// Nederlands. Nooit de browsertaal: de standaard is één taal per gezin, en die is Nederlands.

const KEY_LOCALE = 'fb.locale';

function storedLocale(): LocaleCode {
  try {
    const raw = localStorage.getItem(KEY_LOCALE);
    return isLocaleCode(raw) ? raw : 'nl-NL';
  } catch {
    return 'nl-NL';
  }
}

/** Taal voor schermen zonder gezin (inloggen, registreren); de keuze blijft in deze browser. */
export const browserLocale = signal<LocaleCode>(storedLocale());

export function setBrowserLocale(code: LocaleCode): void {
  browserLocale.value = code;
  try {
    localStorage.setItem(KEY_LOCALE, code);
  } catch {
    // privémodus: dan alleen voor deze pagina
  }
}

/** Taal van het gezin zelf (zonder eigen keuze van een lid). */
export const householdLocale = computed<LocaleCode>(() =>
  household.value ? normalizeLocale(household.value.locale) : browserLocale.value,
);

export const locale = computed<LocaleCode>(() => {
  const h = household.value;
  if (!h) return browserLocale.value;
  const ctx = context.value;
  const memberId = ctx?.households.find((x) => x.id === ctx.active_household_id)?.member_id;
  const own = memberId ? h.members.find((m) => m.id === memberId)?.locale : null;
  return effectiveLocale(h.locale, own ?? null, moduleOn('languages'));
});

/** Cijfers in deze app (Arabisch-Indisch alleen in het Arabisch en alleen als het gezin koos). */
export const digits = computed(() => effectiveDigits(locale.value, household.value?.digits));

export const messages = computed(() => {
  // Eerst de cijfers zetten: elke tekst die hierna getallen invult, gebruikt ze.
  setDisplayDigits(digits.value);
  return messagesFor(locale.value);
});
/** Teksten van de schermen uit sprint 2/3 (packages/i18n app.nl.ts; niet in de kiosk-bundle). */
export const appText = computed(() => {
  setDisplayDigits(digits.value);
  return appMessagesFor(locale.value);
});
/** Intl-tag voor datums en getallen (gregoriaans, gekozen cijfers): "ar-u-ca-gregory-nu-latn". */
export const intlLocale = computed(() => intlTag(locale.value, digits.value));
/** Weer en gebedstijden in de taal van deze login. */
export const wpText = computed(() => weatherPrayerFor(locale.value));

// lang en dir op <html> volgen de taal (Arabisch: rechts-naar-links); de gekozen taal blijft ook
// voor het inlogscherm van deze browser.
if (typeof document !== 'undefined') {
  effect(() => {
    const code = locale.value;
    setRequestLocale(code);
    const info = localeInfo(code);
    const html = document.documentElement;
    if (html.lang !== info.lang) html.lang = info.lang;
    if (html.dir !== info.dir) html.dir = info.dir;
    if (household.value) {
      try {
        localStorage.setItem(KEY_LOCALE, code);
      } catch {
        // niets
      }
    }
  });
}

/** Het gezinslid van deze login in het actieve huishouden (null = niet gekoppeld). */
export const ownMemberId = computed(() => {
  const ctx = context.value;
  if (!ctx) return null;
  return ctx.households.find((h) => h.id === ctx.active_household_id)?.member_id ?? null;
});

/** Teller op "Goedkeuren" in de navigatie (GET /v1/app/approvals → count). */
export const approvalsCount = signal(0);

/** Wordt opgehoogd na elke mutatie; pagina's die ervan afhangen laden dan opnieuw. */
export const dataVersion = signal(0);
export function bumpData(): void {
  dataVersion.value += 1;
}

/** Ververst de goedkeur-teller; fouten zijn stil (de teller is een hint, geen bron). */
export async function refreshApprovals(): Promise<void> {
  try {
    const res = await api.app.approvals();
    approvalsCount.value = res.count;
  } catch {
    // stil: geen verbinding of geen recht
  }
}

/** Navigatie vanuit niet-componentcode; de Shell koppelt dit aan preact-iso's `route()`. */
let navigateImpl: (url: string, replace?: boolean) => void = (url) => {
  window.location.assign(url);
};
export function setNavigate(fn: typeof navigateImpl): void {
  navigateImpl = fn;
}
export function navigate(url: string, replace = false): void {
  navigateImpl(url, replace);
}

export function setAuthenticated(ctx: AuthContext): void {
  context.value = ctx;
  authState.value = 'authenticated';
}

export function clearAuth(): void {
  context.value = null;
  household.value = null;
  authState.value = 'anonymous';
}

/** Haalt de context op; 401 → anoniem. Andere fouten laten de vorige staat staan. */
export async function refreshContext(): Promise<AuthContext | null> {
  if (authState.value === 'unknown') authState.value = 'loading';
  try {
    const ctx = await api.app.auth.context();
    setAuthenticated(ctx);
    return ctx;
  } catch (e) {
    if (isApiError(e, 401)) {
      clearAuth();
      return null;
    }
    if (authState.value === 'loading') authState.value = 'anonymous';
    throw e;
  }
}

export async function loadHousehold(force = false): Promise<HouseholdView> {
  if (household.value && !force) return household.value;
  const h = await api.app.household.get();
  const fixes = duplicateColorFixes(h.members);
  if (fixes.size === 0) {
    household.value = h;
    return h;
  }
  // Oprichter 1 okt ("waarom heeft Anne dezelfde kleur als Baba"): delen twee leden een kleur, dan
  // toont de app de latere meteen in de eerste vrije kleur (het bord doet hetzelfde) en bewaart
  // een ouder die reparatie één keer via de gewone ledenwijziging.
  const shown: HouseholdView = {
    ...h,
    members: h.members.map((m) => {
      const fix = fixes.get(m.id);
      return fix ? { ...m, color_key: fix } : m;
    }),
  };
  household.value = shown;
  void repairColors(h, fixes);
  return shown;
}

const colorRepairTried = new Set<string>();

/** Eenmalig per huishouden en sessie: dubbele kleuren bewaren als verschillend (alleen ouders). */
async function repairColors(h: HouseholdView, fixes: ReadonlyMap<string, string>): Promise<void> {
  const ctx = context.value;
  const active = ctx?.households.find((x) => x.id === ctx.active_household_id);
  if (!active || active.paused || (active.role !== 'owner' && active.role !== 'parent')) return;
  if (colorRepairTried.has(active.id)) return;
  colorRepairTried.add(active.id);
  let saved = 0;
  for (const m of h.members) {
    const color = fixes.get(m.id);
    if (!color) continue;
    try {
      await api.app.members.update(m.id, { color_key: color, if_version: m.version });
      saved += 1;
    } catch {
      // Niet erg: de app en het bord tonen de kleuren al verschillend; volgende sessie opnieuw.
    }
  }
  if (saved > 0) await loadHousehold(true).catch(() => undefined);
}

/**
 * Sessie kwijt of buiten het vertrouwde netwerk (401): naar /login, met uitleg bij
 * `reauth_required` (ONTWERP.md §11.1.1 regel 7).
 */
export function handleAuthLoss(e: unknown): boolean {
  if (!isApiError(e, 401)) return false;
  rememberPairingLocation();
  clearAuth();
  navigate(e.code === 'reauth_required' ? '/login?reason=untrusted_context' : '/login', true);
  return true;
}

export async function logout(forgetBrowser = false): Promise<void> {
  try {
    // 0038: eerst deze browser afmelden voor meldingen (daarna is er geen sessie meer).
    const { forgetPushOnLogout } = await import('../lib/push.js');
    await forgetPushOnLogout();
    await api.app.auth.logout(forgetBrowser);
  } finally {
    clearAuth();
    clearPairingResume();
    sharedComputer.value = false;
    navigate('/login', true);
  }
}
